"use client";
import { EEA_REGIONS, createSiteTags, scriptProxyVia, type SiteTags, type TagManifest } from "@customyai/site";
import { isTagManifest, type AnalyticsIdentifiers } from "./tags-contract";

/** The workspace tags (GA4, Tag Manager, Clarity, Meta…) mount only with the visitor's consent for their
 * category (analytics or marketing) and only through the manifest the site serves at /api/tags. Nothing
 * here runs without the first-party runtime being enabled. */
let runtime: SiteTags | null = null;
let loading: Promise<SiteTags | null> | null = null;
type ConsentReader = () => { analytics: "unknown" | "granted" | "denied"; marketing: "unknown" | "granted" | "denied" };
let readConsent: ConsentReader = () => ({ analytics: "unknown", marketing: "unknown" });
const consent = () => { const c = readConsent(); return { analytics: c.analytics === "granted", marketing: c.marketing === "granted", functional: true }; };

async function loadManifest(): Promise<TagManifest | null> {
  const controller = new AbortController(), timer = setTimeout(() => controller.abort(), 8000);
  try {
    const response = await fetch("/api/tags", { credentials: "omit", cache: "no-store", redirect: "error", signal: controller.signal });
    if (!response.ok) return null;
    const body: unknown = await response.json();
    return isTagManifest(body) ? body : null;
  } catch { return null; } finally { clearTimeout(timer); }
}

export function mountSiteTags(consentReader: ConsentReader): Promise<SiteTags | null> {
  readConsent = consentReader;
  if (runtime) return runtime.updateConsent().then(() => runtime);
  loading ??= (async () => {
    const manifest = await loadManifest();
    const granted = consent();
    if (!manifest || manifest.tags.length === 0 || (!granted.analytics && !granted.marketing)) { loading = null; return null; }
    // Consent Mode v2: denied by default in the EEA/UK/CH until the visitor decides; vendor loaders come
    // through /api/tags/js (first-party, allow-listed by the manifest); withdrawing consent revokes the
    // vendor and expires its cookies (the SDK does it on updateConsent()).
    const tags = createSiteTags({ manifest, consent, onError: () => undefined,
      consentDefaults: { regions: [{ regions: EEA_REGIONS, consent: {} }] }, scriptProxy: scriptProxyVia("/api/tags/js") });
    await tags.mount();
    runtime = tags;
    return tags;
  })();
  return loading;
}
export function updateSiteTagsConsent() { void runtime?.updateConsent(); }
export function siteTagsPage() { runtime?.page(); }
/** Performance reports require analytics consent, independently of marketing consent. */
export function siteTagsMetric(event: string, properties: Record<string, unknown>) {
  if (readConsent().analytics === "granted") runtime?.track(event, properties);
}
export function siteTagsIdentifiers(): AnalyticsIdentifiers | undefined {
  const ids = runtime?.identifiers();
  return ids && Object.keys(ids).length ? ids : undefined;
}
